import { selectFeature } from '@app/store/actions.js';
import { selectingModeSelector } from '@app/store/selectors.js';
import { setUrlUpdatingEnabled } from '@app/url/urlUpdating.js';
import { splitColorAlpha } from '@shared/colorAlpha.js';
import { COLORS } from '@shared/colors.js';
import { RichMarker } from '@shared/components/RichMarker.js';
import { useIconContentProps } from '@shared/drawingIcons.js';
import { SELECTION_COLOR } from '@shared/halo.js';
import { useAppSelector } from '@shared/hooks/useAppSelector.js';
import {
  type LabelTooltipMode,
  labelTooltipMode,
} from '@shared/labelVisibility.js';
import type {
  DragEndEvent,
  LeafletEvent,
  LeafletEventHandlerFnMap,
} from 'leaflet';
import { type ReactElement, useCallback, useMemo } from 'react';
import { Tooltip } from 'react-leaflet';
import { useDispatch } from 'react-redux';
import { drawingPointLabel } from '../labelValues.js';
import type { DrawingPoint } from '../model/actions/drawingPointActions.js';
import {
  drawingMeasure,
  drawingPointChangePosition,
} from '../model/actions/drawingPointActions.js';

export function DrawingPointsResult(): ReactElement {
  const dispatch = useDispatch();

  const interactive0 = useAppSelector(selectingModeSelector);

  const activeIndex = useAppSelector((state) =>
    state.main.selection?.type === 'draw-points'
      ? (state.main.selection.id ?? null)
      : null,
  );

  const handleMove = useCallback(
    (e: LeafletEvent) => {
      if (activeIndex !== null) {
        // see https://github.com/PaulLeCam/react-leaflet/issues/981
        const { latlng } = e as unknown as {
          latlng: { lat: number; lng: number };
        };

        dispatch(
          drawingPointChangePosition({
            index: activeIndex,
            coords: {
              lat: latlng.lat,
              lon: latlng.lng,
            },
          }),
        );

        dispatch(drawingMeasure({ elevation: false }));
      }
    },
    [activeIndex, dispatch],
  );

  const handleDragEnd = useCallback(
    (e: DragEndEvent) => {
      // Re-enable first so the final-position dispatch below runs through the
      // URL processor and commits the whole drag as a single history entry.
      setUrlUpdatingEnabled(true);

      if (activeIndex !== null) {
        const coords = e.target.getLatLng();

        dispatch(
          drawingPointChangePosition({
            index: activeIndex,
            coords: {
              lat: coords.lat,
              lon: coords.lng,
            },
          }),
        );

        dispatch(drawingMeasure({}));
      }
    },
    [activeIndex, dispatch],
  );

  const points = useAppSelector((state) => state.drawingPoints.points);

  const onSelects = useMemo(
    () =>
      new Array(points.length).fill(0).map((_, id) => () => {
        if (id !== activeIndex) {
          dispatch(selectFeature({ type: 'draw-points', id }));

          dispatch(drawingMeasure({}));
        }
      }),
    [points.length, activeIndex, dispatch],
  );

  const change = useAppSelector((state) => state.drawingPoints.change);

  const labelVisibility = useAppSelector(
    (state) => state.drawingSettings.labelVisibility,
  );

  return (
    <>
      {points.map((point, i) => {
        const interactive = interactive0 || activeIndex === i;

        return (
          <DrawingPointMarker
            key={`${change}-${i}`}
            point={point}
            renderColor={point.color || COLORS.normal}
            labelMode={labelTooltipMode(labelVisibility, activeIndex === i)}
            // Selection is the ring around the marker, so the marker itself
            // keeps its own color — the same signal a selected line wears.
            halo={activeIndex === i ? SELECTION_COLOR : undefined}
            interactive={interactive}
            draggable={!window.fmEmbedded && activeIndex === i}
            eventHandlers={{
              dragstart() {
                // Suspend history writes for the whole drag gesture; the
                // intermediate positions (and their measurement fallout) must
                // not pile up in browser history. Re-enabled in handleDragEnd.
                setUrlUpdatingEnabled(false);

                onSelects[i]();
              },
              dragend: handleDragEnd,
              move: handleMove,
              click: onSelects[i],
            }}
          />
        );
      })}
    </>
  );
}

function DrawingPointMarker({
  point,
  point: { coords, label, markerType, icon },
  renderColor,
  labelMode,
  halo,
  interactive,
  draggable,
  eventHandlers,
}: {
  point: DrawingPoint;
  renderColor: string;
  labelMode: LabelTooltipMode | undefined;
  halo?: string;
  interactive: boolean;
  draggable: boolean;
  eventHandlers: LeafletEventHandlerFnMap;
}): ReactElement {
  // The marker interior is the `icon` content: a bundled poi icon, a Font
  // Awesome icon, or up to 2 literal characters of text. The `label` is the
  // descriptive tooltip.
  const contentProps = useIconContentProps(icon);

  // Fade the label tooltip with the same alpha as the marker (0.9 is Leaflet's
  // default tooltip opacity) so the whole marker — including its label — is
  // uniformly transparent.
  const { opacity } = splitColorAlpha(renderColor);

  // A template whose keys all resolve to nothing expands to nothing, so the
  // tooltip is decided by the expanded text — an empty one is a blank box.
  const renderedLabel = label ? drawingPointLabel(point).trim() : '';

  return (
    <RichMarker
      position={{ lat: coords.lat, lng: coords.lon }}
      color={renderColor}
      halo={halo}
      markerType={markerType}
      {...contentProps}
      draggable={draggable}
      interactive={interactive}
      eventHandlers={eventHandlers}
    >
      {renderedLabel && labelMode && (
        <Tooltip
          key={labelMode}
          className="compact multiline"
          direction="top"
          permanent={labelMode === 'permanent'}
          opacity={0.9 * opacity}
        >
          <span>{renderedLabel}</span>
        </Tooltip>
      )}
    </RichMarker>
  );
}
